import { useEffect, useState } from 'react';

import type { Engine } from '@runtime/engine.js';

/** Battery + signal values for the status bar, sourced from the agent host. */
export interface DeviceStatus {
  /** Charge level 0..1. */
  batteryLevel?: number;
  batteryCharging?: boolean;
  /** Signal strength in dBm (negative; closer to 0 is stronger). */
  signalDbm?: number;
}

/** Battery changes slowly; a minute between polls is plenty for a kiosk bar. */
const POLL_INTERVAL_MS = 60_000;

/**
 * Poll the agent host for the battery + signal values the status bar shows.
 *
 * Only the MDM / native host supplies these — the Web Battery API has been
 * removed from Chrome and there is no web signal API — so on browser / demo
 * hosts every field stays undefined and the bar renders nothing rather than a
 * misleading stub. Feature-detected and tolerant exactly like
 * {@link useCurrentUser}: returns empty when there's no engine, no host
 * capability, or the call rejects.
 *
 * `enabled` gates polling so devices whose config doesn't show these
 * indicators never touch the bridge.
 */
export function useDeviceStatus(engine: Engine | undefined, enabled: boolean): DeviceStatus {
  const [status, setStatus] = useState<DeviceStatus>({});

  useEffect(() => {
    const host = engine?.agentHost;
    const fetcher = host?.getDeviceProperties;
    if (!enabled || host === undefined || fetcher === undefined) {
      setStatus({});
      return undefined;
    }
    let cancelled = false;
    const poll = (): void => {
      void fetcher.call(host).then(
        (p) => {
          if (cancelled) return;
          const next: DeviceStatus = {};
          if (p.batteryLevel !== undefined) next.batteryLevel = p.batteryLevel;
          if (p.batteryCharging !== undefined) next.batteryCharging = p.batteryCharging;
          if (p.signalDbm !== undefined) next.signalDbm = p.signalDbm;
          setStatus(next);
        },
        () => {
          if (!cancelled) setStatus({});
        },
      );
    };
    poll();
    const id = setInterval(poll, POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [engine, enabled]);

  return status;
}
